import React, { useState } from "react";
import { motion } from "framer-motion";
import { MOCK_AUDIT_LOGS } from "../../lib/mockData";
import { formatDateTime, truncateHash, cn } from "../../lib/utils";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { ScrollArea } from "../ui/scroll-area";
import {
  CheckCircle2,
  Clock,
  FileText,
  Shield,
  User,
  Building2,
  Heart,
  FolderOpen,
  Link as LinkIcon,
} from "lucide-react";

const entityIcons = {
  donation: Heart,
  project: FolderOpen,
  organization: Building2,
  user: User,
};

const actionColors = {
  created: "bg-green-500/20 text-green-500 border-green-500/30",
  updated: "bg-blue-500/20 text-blue-500 border-blue-500/30",
  verified: "bg-emerald-500/20 text-emerald-500 border-emerald-500/30",
  status_updated: "bg-purple-500/20 text-purple-500 border-purple-500/30",
  disbursed: "bg-amber-500/20 text-amber-500 border-amber-500/30",
};

export const AuditTrail = () => {
  const [logs] = useState(MOCK_AUDIT_LOGS);
  const chainValid = true;

  return (
    <Card data-testid="audit-trail">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg font-heading">Audit Trail</CardTitle>
          </div>
          <Badge
            variant="outline"
            className="flex items-center gap-1 bg-green-500/20 text-green-500 border-green-500/30"
          >
            <CheckCircle2 className="h-3 w-3" />
            Chain Verified
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Immutable record of all transactions and changes
        </p>
      </CardHeader>

      <CardContent>
        <ScrollArea className="h-[500px] pr-4">
          <div className="relative">
            <div className="absolute left-4 top-0 bottom-0 w-px bg-border" />
            <div className="space-y-4">
              {logs.map((log, index) => {
                const EntityIcon = entityIcons[log.entity_type] || FileText;
                return (
                  <motion.div
                    key={log.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className="relative pl-10"
                    data-testid={`audit-log-${log.id}`}
                  >
                    <div className="absolute left-0 top-2 h-8 w-8 rounded-full bg-card border-2 border-border flex items-center justify-center">
                      <EntityIcon className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="p-4 rounded-lg bg-card border border-border hover:border-primary/30 transition-colors">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className={actionColors[log.action] || ""}>
                              {log.action.replace("_", " ")}
                            </Badge>
                            <Badge variant="secondary" className="capitalize">
                              {log.entity_type}
                            </Badge>
                          </div>
                          <p className="mt-2 text-sm">
                            <span className="text-muted-foreground">By </span>
                            <span className="font-medium">{log.actor_name}</span>
                          </p>
                          {Object.keys(log.details || {}).length > 0 && (
                            <div className="mt-2 text-xs text-muted-foreground">
                              {Object.entries(log.details).map(([key, value]) => (
                                <span key={key} className="mr-3">
                                  <span className="capitalize">{key.replace("_", " ")}: </span>
                                  <span className="text-foreground">
                                    {typeof value === "number" ? value.toLocaleString() : String(value)}
                                  </span>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            {formatDateTime(log.created_at)}
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-border flex items-center justify-between text-[10px] font-mono text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <LinkIcon className="h-3 w-3" />
                          <span>Block: {truncateHash(log.block_hash, 12, 8)}</span>
                        </div>
                        {log.previous_hash && (
                          <span>Prev: {truncateHash(log.previous_hash, 8, 6)}</span>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default AuditTrail;
